import { Button, EmptyState, Field, Input, Spinner } from '@nexo/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarClock, CalendarX } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Logo } from '../../components/Logo';
import { api } from '../../lib/api';
import { formatDay, formatTime } from '../../lib/format';
import { standaloneCallUrl } from '../calls/callStore';
import { errorMessage } from '../../lib/forms';

const NAME_KEY = 'nexo.guestName';

/** Public page behind a meeting guest link: name → straight into the call. */
export function GuestMeetingPage() {
  const { token = '' } = useParams();
  const info = useQuery({
    queryKey: ['guest-meeting', token],
    queryFn: () => api.meetings.guestInfo(token),
    retry: false,
    refetchInterval: 30_000,
  });
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [error, setError] = useState<string>();
  const { t } = useTranslation();

  const join = useMutation({
    mutationFn: () => api.meetings.guestJoin(token, name.trim()),
    onSuccess: (credentials) => {
      localStorage.setItem(NAME_KEY, name.trim());
      window.location.assign(
        standaloneCallUrl(credentials, info.data?.title ?? t('Reunión'), false),
      );
    },
    onError: (err) => setError(errorMessage(err)),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    if (!name.trim()) return setError(t('Escribe tu nombre'));
    join.mutate();
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-bg p-6">
      <Logo />
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-panel">
        {info.isPending ? (
          <div className="flex justify-center py-6 text-muted">
            <Spinner />
          </div>
        ) : !info.data ? (
          <EmptyState
            icon={<CalendarX className="size-6" />}
            title={t('Enlace no válido')}
            description={t('Pide a quien organiza la reunión que te envíe un enlace nuevo.')}
          />
        ) : (
          <>
            <p className="text-xs font-medium tracking-wide text-subtle uppercase">
              {t('{{team}} te invita a una reunión', { team: info.data.teamName })}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">{info.data.title}</h1>
            <p className="mt-2 flex items-center gap-1.5 text-sm text-muted">
              <CalendarClock className="size-4" />
              {formatDay(info.data.startsAt)} · {formatTime(info.data.startsAt)} –{' '}
              {formatTime(info.data.endsAt)} ·{' '}
              {t('organiza {{name}}', { name: info.data.organizer })}
            </p>
            {info.data.cancelled ? (
              <p className="mt-5 text-sm font-medium text-danger">
                {t('Esta reunión se ha cancelado.')}
              </p>
            ) : !info.data.open ? (
              <p className="mt-5 rounded-lg bg-surface-2 p-3 text-sm text-muted">
                {t(
                  'Podrás entrar desde 15 minutos antes del inicio. Deja esta página abierta: se actualiza sola.',
                )}
              </p>
            ) : (
              <form onSubmit={onSubmit} noValidate className="mt-5 flex flex-col gap-3">
                <Field label={t('Tu nombre')} error={error}>
                  {(props) => (
                    <Input
                      {...props}
                      autoFocus
                      maxLength={60}
                      placeholder={t('Como quieres que te vean')}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setError(undefined);
                      }}
                    />
                  )}
                </Field>
                <Button type="submit" size="lg" loading={join.isPending}>
                  {t('Entrar en la reunión')}
                </Button>
                <p className="text-xs text-subtle">
                  {t(
                    'No necesitas cuenta. Tu navegador te pedirá permiso para usar el micrófono y la cámara.',
                  )}
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
